import { useCallback, useEffect, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Select from 'react-select';
import { GripVertical, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { publicFetch, apiFetch } from '../lib/api';

type Card = {
  challengeId: string;
  text: string;
  challengeName: string;
};

function SortableRow({
  card,
  onRemove,
}: {
  card: Card;
  onRemove: (challengeId: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: card.challengeId });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm"
    >
      <button
        type="button"
        className="text-slate-400 hover:text-ink"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-5 w-5" />
      </button>
      <span className="flex-1 text-sm">{card.text}</span>
      <button
        type="button"
        onClick={() => onRemove(card.challengeId)}
        className="rounded p-1 text-red-500 hover:bg-red-50"
        aria-label="Remove"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function HomePageEditor() {
  const [cards, setCards] = useState<Card[]>([]);
  const [other, setOther] = useState<Card[]>([]);
  const [loading, setLoading] = useState(true);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const chRes = await publicFetch('/challenge');
        const challenges: Record<string, unknown>[] = await chRes.json();
        const hpRes = await publicFetch('/homepage');
        const hp = await hpRes.json();
        const order: string[] = hp.Data || [];
        const onHome: Card[] = [];
        const offHome: Card[] = [];
        challenges.forEach((c) => {
          const challengeId = String(c.ChallengeId);
          const row: Card = {
            challengeId,
            text: `${c.Name} — ${challengeId}`,
            challengeName: String(c.Name),
          };
          if (order.includes(challengeId)) {
            onHome.push(row);
          } else {
            offHome.push(row);
          }
        });
        onHome.sort(
          (a, b) => order.indexOf(a.challengeId) - order.indexOf(b.challengeId),
        );
        if (!cancelled) {
          setCards(onHome);
          setOther(offHome);
        }
      } catch (e) {
        toast.error(String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const save = useCallback(async () => {
    try {
      const res = await apiFetch('/homepage', {
        method: 'POST',
        body: JSON.stringify({
          ChallengeIds: cards.map((c) => c.challengeId),
        }),
      });
      if (!res.ok) {
        const err = await res.text();
        throw new Error(err || res.statusText);
      }
      await res.json();
      toast.success('Homepage order saved');
    } catch (e) {
      toast.error(String(e));
    }
  }, [cards]);

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setCards((items) => {
      const oldIndex = items.findIndex((i) => i.challengeId === active.id);
      const newIndex = items.findIndex((i) => i.challengeId === over.id);
      return arrayMove(items, oldIndex, newIndex);
    });
  }

  function remove(challengeId: string) {
    setCards((c) => c.filter((x) => x.challengeId !== challengeId));
    const removed = cards.find((x) => x.challengeId === challengeId);
    if (removed) setOther((o) => [...o, removed]);
  }

  function addFromDropdown(opt: Card) {
    setCards((c) => [...c, opt]);
    setOther((o) => o.filter((x) => x.challengeId !== opt.challengeId));
  }

  if (loading) return <p className="text-ink-muted">Loading homepage…</p>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-ink">Homepage challenge order</h1>
        <p className="text-sm text-ink-muted">
          Drag to reorder. Saves to{' '}
          <code className="rounded bg-slate-100 px-1 text-xs">POST /homepage</code>.
        </p>
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={cards.map((c) => c.challengeId)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {cards.map((card) => (
              <SortableRow key={card.challengeId} card={card} onRemove={remove} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <div className="max-w-lg">
        <p className="mb-2 text-sm font-medium text-ink">Add challenge</p>
        <Select
          value={null}
          options={other.map((c) => ({ label: c.text, value: c }))}
          onChange={(opt) => opt && addFromDropdown(opt.value)}
          placeholder="Choose a challenge…"
        />
      </div>
      <button
        type="button"
        onClick={save}
        className="rounded-lg bg-brand px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"
      >
        Save order
      </button>
    </div>
  );
}
